import { Sparkles } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * AliceLauncher — the minimized / closed-state assistant launcher.
 *
 * A polished floating pill: white background, soft border, subtle shadow,
 * rounded-full. Contains the Alice avatar, an "Ask Alice" label, a live
 * status dot, and an optional unread notification badge. Click opens the
 * AliceDrawer (via `onClick`). Presentation only — wire `onClick` to your
 * app's Alice state setter.
 *
 * This is the canonical minimized state. Pair with AliceDrawer (docked) or
 * use AliceAssistant which composes both states automatically.
 */
export function AliceLauncher({
  name = 'Alice',
  label = 'Ask Alice',
  avatarSrc,
  status = 'online',
  unread,
  onClick,
  position = 'fixed',
  className,
}: {
  name?: string;
  label?: string;
  avatarSrc?: string;
  status?: 'online' | 'thinking' | 'offline';
  unread?: number;
  onClick?: () => void;
  /** 'fixed' for a real app, 'absolute' to contain it inside a preview frame. */
  position?: 'fixed' | 'absolute';
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open ${name}`}
      className={cn(
        'group z-30 flex items-center gap-2.5 rounded-full border border-border bg-card py-1.5 pl-1.5 pr-4 shadow-[var(--mui-shadow-pop)] transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        position === 'fixed' ? 'fixed bottom-5 right-5' : 'absolute bottom-4 right-4',
        className,
      )}
    >
      <span className="relative flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-primary">
        {avatarSrc ? (
          <img src={avatarSrc} alt={`${name} avatar`} className="h-full w-full object-cover" />
        ) : (
          <Sparkles className="h-4 w-4" aria-hidden />
        )}
        <span
          aria-hidden
          className={cn(
            'absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-card',
            status === 'online' && 'bg-[#16a34a]',
            status === 'thinking' && 'bg-warning',
            status === 'offline' && 'bg-muted-foreground/50',
          )}
        />
      </span>
      <span className="text-[13px] font-medium text-foreground">{label}</span>
      {typeof unread === 'number' && unread > 0 && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );
}

/** @deprecated Use AliceLauncher instead. */
export { AliceLauncher as AliceBottomLauncher };
